import React, { useCallback, useId } from 'react';
import { useSelector } from 'react-redux';

import { Box, Button, Dialog, Typography } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { KEY_CODES } from 'constants/keyboard';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';
import { getPressedKey, isEditableTarget } from 'utils/keyboard';

import { getConfirmationDialogState } from 'store/global/selectors';

import {
  confirmationIconComponentsMap,
  KEYBOARD_OPT_OUT_SELECTOR,
  PaperProps,
  slotProps,
} from './ConfirmationModalCDS.constants';
import { ConfirmationModalTestIds } from './ConfirmationModalCDS.testIds';

const ConfirmationModalCDS = ({ onClose, onAcceptClick, dataTestId }) => {
  const confirmationDialogState = useSelector(getConfirmationDialogState);

  const {
    isOpen,
    message,
    description,
    customButtons,
    customDialogProps,
    type = ConfirmationModalType.INFO,
    onAccept,
    onAcceptButtonName,
    onCancelButtonName = 'Cancel',
    body,
  } = confirmationDialogState;

  const IconComponent = confirmationIconComponentsMap[type];

  const isAcknowledgement = !onAccept;
  const isCancelHidden = isAcknowledgement || type === ConfirmationModalType.ERROR;
  const acceptButtonName = onAcceptButtonName ?? (isAcknowledgement ? 'Ok' : 'Confirm');
  const isKeyboardOwnedByModal = !body && !customButtons;

  const instanceId = useId();
  const titleId = `${instanceId}-title`;
  const descriptionId = `${instanceId}-description`;

  const onKeyDown = useCallback(
    e => {
      if (e.repeat || getPressedKey(e) !== KEY_CODES.ENTER) {
        return;
      }

      if (isEditableTarget(e.target) || e.target.closest(KEYBOARD_OPT_OUT_SELECTOR)) {
        return;
      }

      onAcceptClick(e);
    },
    [onAcceptClick],
  );

  return (
    <Dialog
      PaperProps={PaperProps}
      aria-describedby={!body && description ? descriptionId : null}
      aria-labelledby={body ? null : titleId}
      data-testid={dataTestId || ConfirmationModalTestIds.MODAL}
      disableEscapeKeyDown={!isKeyboardOwnedByModal}
      open={isOpen}
      scroll="body"
      slotProps={slotProps}
      onClose={onClose}
      onKeyDownCapture={isKeyboardOwnedByModal ? onKeyDown : null}
      {...customDialogProps}
    >
      <Box className="dialog-wrapper flex flex-col items-center gap-24 p-24 w-full">
        <Box className="dialog-content flex flex-col items-center gap-16 w-full">
          <IconComponent size={IconSize[64]} />

          {body ?? (
            <Box className="dialog-text-content flex flex-col items-center gap-16 w-full">
              <Typography
                component="h5"
                data-testid={ConfirmationModalTestIds.MESSAGE_LABEL}
                id={titleId}
                variant="bold16"
              >
                {message}
              </Typography>

              {description &&
                (_.isString(description) ? (
                  <Typography
                    color="text.secondary"
                    component="p"
                    data-testid={ConfirmationModalTestIds.DESCRIPTION_LABEL}
                    id={descriptionId}
                    variant="regular14"
                  >
                    {description}
                  </Typography>
                ) : (
                  <Box
                    className="w-full"
                    data-testid={ConfirmationModalTestIds.DESCRIPTION_LABEL}
                    id={descriptionId}
                  >
                    {description}
                  </Box>
                ))}
            </Box>
          )}
        </Box>

        {body
          ? null
          : (customButtons ?? (
              <ButtonGroupWrapper
                alignment={HORIZONTAL_ALIGNMENT.CENTER}
                className="dialog-actions w-full"
              >
                {!isCancelHidden && (
                  <Button
                    className="dialog-action flex-1"
                    data-testid={ConfirmationModalTestIds.CANCEL_BUTTON}
                    size="large"
                    variant="line"
                    onClick={onClose}
                  >
                    {onCancelButtonName}
                  </Button>
                )}
                <Button
                  className="dialog-action flex-1"
                  data-testid={ConfirmationModalTestIds.ACCEPT_BUTTON}
                  size="large"
                  onClick={onAcceptClick}
                >
                  {acceptButtonName}
                </Button>
              </ButtonGroupWrapper>
            ))}
      </Box>
    </Dialog>
  );
};

// TODO: move handlers from props when legacy dialogs will be fully deprecated
ConfirmationModalCDS.propTypes = {
  onClose: PropTypes.func.isRequired,
  onAcceptClick: PropTypes.func.isRequired,
  dataTestId: PropTypes.string,
};

export default ConfirmationModalCDS;
